import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Skeleton } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { Security } from '../../proto/generated/security'
import {
    countActions,
    mapScoreToLabel,
    mergeActionCounts,
    scoreFromActions
} from '../../util/technicals'
import useOverviewPanelNavigation from '../../hooks/useOverviewPanelNavigation'
import { useTechnicalsData } from '../../pages/technicals/engine/useTechnicalsData'
import TechnicalsSummaryCard from './TechnicalsSummaryCard'
import OverviewSectionHeader from './OverviewSectionHeader'
import { OverviewSection, OVERVIEW_SECTION_IDS, OVERVIEW_SECTION_ORDER } from './OverviewSectionNav'

type TechnicalsSectionProps = {
    security: Security | undefined
}

const OVERVIEW_TIMEFRAME = '1D'

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(0, 2, 2)
    },
    gauge: {
        maxWidth: 220,
        margin: '0 auto'
    },
    moreButton: {
        marginTop: theme.spacing(1.5),
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        justifyContent: 'space-between'
    }
}))

const TechnicalsSection = ({ security }: TechnicalsSectionProps) => {
    const { classes } = useStyles()
    const openTechnicalsPanel = useOverviewPanelNavigation('technicals')

    const { result, loading } = useTechnicalsData({
        security,
        timeframeId: OVERVIEW_TIMEFRAME,
        enabled: !!security
    })

    const handleOpenTechnicals = useCallback(() => {
        openTechnicalsPanel(security)
    }, [openTechnicalsPanel, security])

    const summary = useMemo(() => {
        const oscillatorRows = result?.oscillators ?? []
        const movingAverageRows = result?.movingAverages ?? []
        if (oscillatorRows.length === 0 && movingAverageRows.length === 0) return null

        const oscillatorScore = scoreFromActions(oscillatorRows)
        const movingAverageScore = scoreFromActions(movingAverageRows)
        const score = (oscillatorScore + movingAverageScore) / 2

        return {
            score,
            label: mapScoreToLabel(score),
            counts: mergeActionCounts(countActions(oscillatorRows), countActions(movingAverageRows))
        }
    }, [result])

    // Keep the previous gauge on screen while the next security loads, like the chart and
    // range sections do, instead of collapsing the section.
    const lastSummaryRef = useRef<typeof summary>(null)
    useEffect(() => {
        if (summary) {
            lastSummaryRef.current = summary
        } else if (!loading) {
            lastSummaryRef.current = null
        }
    }, [summary, loading])

    const displaySummary = summary ?? (loading ? lastSummaryRef.current : null)

    if (!security) return null

    if (!displaySummary) {
        if (!loading) return null
        return (
            <OverviewSection
                id={OVERVIEW_SECTION_IDS.technicals}
                label="Technicals"
                order={OVERVIEW_SECTION_ORDER.technicals}
            >
            <Box boxShadow={0}>
                <OverviewSectionHeader title="Technicals" />
                <Box className={classes.container}>
                    <Skeleton variant="rounded" height={160} />
                </Box>
            </Box>
            </OverviewSection>
        )
    }

    return (
        <OverviewSection
            id={OVERVIEW_SECTION_IDS.technicals}
            label="Technicals"
            order={OVERVIEW_SECTION_ORDER.technicals}
        >
        <Box boxShadow={0} sx={{ opacity: loading ? 0.72 : 1, transition: 'opacity 0.2s ease' }}>
            <OverviewSectionHeader title="Technicals" />
            <Box className={classes.container}>
                <Box className={classes.gauge}>
                    <TechnicalsSummaryCard
                        title="Summary"
                        label={displaySummary.label}
                        score={displaySummary.score}
                        counts={displaySummary.counts}
                        emphasized
                        showTitle={false}
                        showCounts={false}
                        animated={false}
                    />
                </Box>
                <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    size="small"
                    className={classes.moreButton}
                    endIcon={<ChevronRightIcon fontSize="small" />}
                    onClick={handleOpenTechnicals}
                >
                    More technicals
                </Button>
            </Box>
        </Box>
        </OverviewSection>
    )
}

export default memo(TechnicalsSection)
